import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const plans = [
  {
    name: "Private Seller",
    price: "AUD $98",
    period: "per listing/month",
    badge: null,
    description: "Perfect for private sellers looking to list their business",
    features: [
      "1 Active Business Listing",
      "AI-Powered Buyer Matching",
      "Lead Management Dashboard",
      "Email Notifications",
      "Basic Analytics",
      "Standard Support"
    ],
    leadPrice: "AUD $19 per qualified lead",
    cta: "Start Listing",
    popular: false
  },
  {
    name: "Broker Package",
    price: "AUD $89",
    period: "per listing/month",
    badge: "Most Popular",
    description: "For licensed business brokers managing multiple listings",
    features: [
      "Unlimited Active Listings",
      "Priority Buyer Matching",
      "Advanced Lead Analytics",
      "SMS + Email Notifications",
      "Team Member Access",
      "Priority Support",
      "Custom Branding",
      "Lead Export Tools"
    ],
    leadPrice: "AUD $15 per qualified lead",
    cta: "Start Your Listings",
    popular: true
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "pricing",
    badge: "Best Value",
    description: "For large brokerages and high-volume sellers",
    features: [
      "Volume Discounts Available",
      "Dedicated Account Manager",
      "White-Label Solution",
      "API Access",
      "Advanced Team Management",
      "Custom Integrations",
      "Dedicated Support",
      "SLA Guarantees"
    ],
    leadPrice: "Volume pricing available",
    cta: "Contact Sales",
    popular: false
  }
];

export function PricingSection() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handlePlanClick = (planName: string, cta: string) => {
    if (cta === "Contact Sales") {
      navigate("/contact");
    } else {
      // If user is authenticated, take them to list business page
      // If not authenticated, take them to sign up
      navigate(user ? "/list-business" : "/sign-up");
    }
  };
  return (
    <section id="pricing" className="py-20 bg-gradient-card">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            No hidden fees, no long-term contracts. Pay only for results.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <Card 
              key={index} 
              className={`relative group hover:shadow-large transition-all duration-300 hover:-translate-y-2 ${
                plan.popular 
                  ? 'border-primary shadow-medium scale-105' 
                  : 'border-border/50'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge variant="secondary" className="bg-gradient-gold text-secondary-foreground font-semibold px-4 py-1">
                    <Star className="w-3 h-3 mr-1" />
                    {plan.badge}
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl font-bold text-foreground mb-2">
                  {plan.name}
                </CardTitle>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-primary">{plan.price}</span>
                  <span className="text-muted-foreground ml-2">{plan.period}</span>
                </div>
                <p className="text-muted-foreground text-sm">
                  {plan.description}
                </p>
              </CardHeader>

              <CardContent className="pt-0">
                <div className="mb-6 p-3 bg-secondary/10 rounded-lg text-center">
                  <div className="text-sm font-medium text-secondary-foreground">
                    Lead Pricing
                  </div>
                  <div className="text-lg font-bold text-secondary">
                    {plan.leadPrice}
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  variant={plan.popular ? "premium" : "hero"} 
                  className="w-full"
                  size="lg"
                  onClick={() => handlePlanClick(plan.name, plan.cta)}
                >
                  {plan.cta}
                </Button>

              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-muted-foreground">
            All plans include: Secure payments • Mobile app access • Lead verification • Cancel anytime
          </p>
        </div>
      </div>
    </section>
  );
}